import React, {
    createContext,
    useContext,
    ReactNode,
    useMemo,
    useCallback,
    useState,
    useEffect,
} from 'react';
import { RootState } from '../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { typesRedux } from '../shared/types/typesRedux';
import { asyncGetUserLog } from '../services/general.service';

interface Props {
    children?: ReactNode;
}

export const TOKEN = 'My_Token';
export const THEME = 'Mode_Theme';
const EXPIRATION_TOKEN = 'Expiration_Token';

export const AuthContext = createContext<any>(null);

export default function AuthContextProvider({ children }: Props) {
    const dispatch = useDispatch();
    const { token, exp, expirationToken } = useSelector(
        (state: RootState) => state.auth
    );
    const { mode, logUser } = useSelector((state: RootState) => state.general);

    const [tokenAuth, setTokenAuth] = useState<string | null>(
        localStorage.getItem(TOKEN)
    );
    const [dateExpirationToken, setDateExpirationToken] = useState<Date | null>(
        localStorage.getItem(EXPIRATION_TOKEN)
            ? new Date(localStorage.getItem(EXPIRATION_TOKEN)!)
            : null
    );
    const [modeTheme, setModeTheme] = useState<string | null>(
        localStorage.getItem(THEME)
    );

    useEffect(() => {
        if (tokenAuth) {
            localStorage.setItem(TOKEN, tokenAuth);
        } else {
            localStorage.removeItem(TOKEN);
        }
    }, [tokenAuth]);

    useEffect(() => {
        if (dateExpirationToken) {
            localStorage.setItem(
                EXPIRATION_TOKEN,
                dateExpirationToken.toISOString()
            );
        } else {
            localStorage.removeItem(EXPIRATION_TOKEN);
        }
    }, [dateExpirationToken]);

    const logout = useCallback(() => {
        setTokenAuth(null);
        setDateExpirationToken(null);
        localStorage.removeItem(TOKEN);
        localStorage.removeItem(EXPIRATION_TOKEN);
    }, []);

    useEffect(() => {
        if (token) {
            setTokenAuth(token);
            setDateExpirationToken(
                exp ? new Date(exp) : new Date(Date.now() + 3600 * 1000)
            );
        }
        if (mode) setModeTheme(mode);

        if (logUser === undefined && (token || tokenAuth)) {
            asyncGetUserLog().then((body: any) => {
                if (body.items) {
                    dispatch({
                        type: typesRedux.USER_LOG,
                        payload: body.items[0],
                    });
                }
            });
        }
    }, [dispatch, token, exp, mode, logUser]);

    useEffect(() => {
        if (dateExpirationToken) {
            const timer = window.setInterval(() => {
                if (
                    expirationToken !== true &&
                    dateExpirationToken <= new Date()
                ) {
                    dispatch({ type: typesRedux.VALIDATION_TOKEN });
                    logout();
                }
            }, 6000);
            return () => window.clearInterval(timer);
        }
    }, [expirationToken, dateExpirationToken, dispatch, logout]);

    const value = useMemo(
        () => ({
            tokenAuth,
            setTokenAuth,
            dateExpirationToken,
            setDateExpirationToken,
            modeTheme,
            logout,
        }),
        [
            tokenAuth,
            setTokenAuth,
            dateExpirationToken,
            setDateExpirationToken,
            modeTheme,
            logout,
        ]
    );

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
}

export function useAuthContext() {
    return useContext(AuthContext);
}
